import { useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { EASE } from "./Reveal";

export const ActionButton = ({ children, onClick, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className="group relative mt-10 overflow-hidden border border-white/20 px-8 py-4 font-mono text-[10px] tracking-[0.35em] text-white transition-colors duration-700 hover:border-[#D4AF37]/70 hover:text-[#D4AF37]"
  >
    <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-[#D4AF37]/10 to-transparent transition-transform duration-1000 group-hover:translate-x-full" />
    {children}
  </button>
);

const Overlay = ({ open, onClose, title, label, testid, z = 96, esc = true, children }) => {
  useEffect(() => {
    if (!open) return;
    window.__lenis?.stop();
    return () => window.__lenis?.start();
  }, [open]);

  useEffect(() => {
    if (!open || !esc) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, esc, onClose]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid={testid}
          data-lenis-prevent
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          style={{ zIndex: z }}
          className="fixed inset-0 overflow-y-auto bg-[#030305]"
          onClick={onClose}
        >
          <div
            className="mx-auto min-h-full max-w-6xl px-6 py-24 md:px-12"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid={`${testid}-close`}
              onClick={onClose}
              aria-label="Close"
              className="fixed right-6 top-6 flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-[#A0A0B0] transition-colors duration-500 hover:border-[#D4AF37]/60 hover:text-[#D4AF37]"
            >
              <X size={18} strokeWidth={1.25} />
            </button>
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, ease: EASE }}
            >
              {label && (
                <span className="font-mono text-[10px] tracking-[0.4em] text-[#D4AF37]/80 uppercase">
                  {label}
                </span>
              )}
              <button
                data-testid={`${testid}-title-back`}
                onClick={onClose}
                aria-label="Back to previous section"
                className="group mt-4 block text-left"
              >
                <h2 className="font-serif text-4xl tracking-wide text-[#F5F5FA] transition-colors duration-500 group-hover:text-[#D4AF37] md:text-5xl">
                  {title}
                </h2>
                <span className="mt-4 block h-px w-12 bg-[#D4AF37]/30 transition-all duration-700 group-hover:w-28 group-hover:bg-[#D4AF37]/80" />
              </button>
              <div className="mt-14">{children}</div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default Overlay;
